import { Download, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Dialog } from 'vnetwork-ui'
import { getEInvoice } from '../../services'
import { PDF_VIEW_PARAMS } from '../../utils/e-invoice'

interface EInvoicePdfDialogProps {
    open: boolean
    onClose: () => void
    invoiceID: number
    title: string
}

/**
 * Xem PDF hoá đơn điện tử ngay trên giao diện.
 *
 * `pdf_url` backend presign kèm `Content-Disposition: attachment` (tải về đúng tên file), nên mở
 * trực tiếp / nhúng iframe đều bị tải xuống. Ở đây lấy link mới (hết hạn 1 giờ), tải file về thành
 * blob `application/pdf` rồi hiện bằng viewer của trình duyệt. Bucket cho phép CORS mọi origin.
 */
const EInvoicePdfDialog = ({
    open,
    onClose,
    invoiceID,
    title,
}: EInvoicePdfDialogProps) => {
    const { t } = useTranslation('invoice')
    const [objectURL, setObjectURL] = useState<string>()
    const [downloadURL, setDownloadURL] = useState<string>()
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        let cancelled = false
        let createdURL: string | undefined

        const load = async () => {
            try {
                const pdfURL = (await getEInvoice(invoiceID))?.result?.pdf_url
                if (!pdfURL) throw new Error('missing pdf_url')
                const response = await fetch(pdfURL)
                if (!response.ok) throw new Error(`HTTP ${response.status}`)
                const blob = new Blob([await response.arrayBuffer()], {
                    type: 'application/pdf',
                })
                if (cancelled) return
                createdURL = URL.createObjectURL(blob)
                setObjectURL(createdURL)
                setDownloadURL(pdfURL)
            } catch {
                if (!cancelled) setFailed(true)
            }
        }
        load()

        return () => {
            cancelled = true
            if (createdURL) URL.revokeObjectURL(createdURL)
        }
    }, [invoiceID])

    const renderContent = () => {
        if (failed)
            return (
                <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                    {t('e-invoice-pdf-load-failed')}
                </div>
            )
        if (!objectURL)
            return (
                <div className="flex h-[70vh] items-center justify-center text-gray-500">
                    <Loader2 className="h-6 w-6 animate-spin" />
                </div>
            )
        return (
            <iframe
                title={title}
                src={`${objectURL}${PDF_VIEW_PARAMS}`}
                className="h-[70vh] w-full border-0 bg-white"
            />
        )
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            title={title}
            actions={
                <div className="flex w-full justify-end gap-2">
                    <Button variant="outlined" onClick={onClose}>
                        {t('close')}
                    </Button>
                    {downloadURL && (
                        <Button
                            startIcon={<Download className="h-4 w-4" />}
                            // Link presign có tên file sẵn (hoa-don-<ký hiệu>-<số>.pdf)
                            onClick={() => window.location.assign(downloadURL)}
                        >
                            {t('e-invoice-download-pdf')}
                        </Button>
                    )}
                </div>
            }
        >
            {renderContent()}
        </Dialog>
    )
}

export default EInvoicePdfDialog
